import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X, LogOut, Settings } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth, useCart, useWishlist } from "../lib/store";
import { api } from "../lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/op-preorder-jp", label: "OP Preorder JP" },
  { to: "/op-preorder-en", label: "OP Preorder EN" },
  { to: "/singles", label: "Singles" },
  { to: "/pkm-sealed-it", label: "PKM Sealed IT" },
  { to: "/pkm-sealed-jp", label: "PKM Sealed JP" },
  { to: "/accessori", label: "Accessori" },
  { to: "/richieste", label: "Richieste" },
  { to: "/community", label: "Community" },
];

export const Header = ({ onOpenAuth }) => {
  const [scrolled, setScrolled] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sugg, setSugg] = useState([]);
  const [showUser, setShowUser] = useState(false);

  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const { count: wlCount } = useWishlist();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    let t;
    if (q && q.length >= 2) {
      t = setTimeout(async () => {
        try {
          const { data } = await api.get(`/products/suggest`, { params: { q } });
          setSugg(data);
        } catch {}
      }, 200);
    } else setSugg([]);
    return () => clearTimeout(t);
  }, [q]);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setDrawer(false);
    }
  };

  return (
    <>
      {/* Announcement bar */}
      <div className="w-full bg-black text-[10px] sm:text-[11px] font-mono-ps tracking-[0.25em] uppercase text-[#C9A227]/80 border-b border-[#C9A227]/15">
        <div className="max-w-7xl mx-auto px-4 py-2 flex justify-between items-center">
          <span className="hidden sm:inline">Vinted · BRT · BRT Assicurata</span>
          <span>Trova. Acquista. Colleziona.</span>
          <span className="hidden sm:inline">Prodotti 100% autentici</span>
        </div>
      </div>

      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 backdrop-blur-md bg-[#090909]/92 border-b border-[#C9A227]/20 transition-all duration-300 ${
          scrolled ? "py-1.5" : "py-3"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-4">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setDrawer(true)}
            className="lg:hidden p-2 -ml-2 text-white"
            aria-label="Menu"
          >
            <Menu size={22} />
          </button>

          <Logo compact={scrolled} />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5 ml-4 flex-1 overflow-x-auto">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-").replace("é", "e")}`}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 text-[12px] font-mono-ps uppercase tracking-[0.18em] transition-colors whitespace-nowrap ${
                    isActive ? "text-[#C9A227]" : "text-white/70 hover:text-[#C9A227]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-1">
            <button
              data-testid="search-toggle"
              aria-label="Cerca"
              onClick={() => setSearchOpen((s) => !s)}
              className="p-2 text-white hover:text-[#C9A227] transition-colors"
            >
              <Search size={19} />
            </button>

            <div className="relative">
              <button
                data-testid="account-toggle"
                aria-label="Account"
                onClick={() => (user ? setShowUser((s) => !s) : onOpenAuth())}
                className="p-2 text-white hover:text-[#C9A227] transition-colors"
              >
                <User size={19} />
              </button>
              {user && showUser && (
                <div className="absolute right-0 mt-2 w-56 bg-[#121212] border border-[#C9A227]/25 rounded-sm shadow-2xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#C9A227]/15">
                    <div className="text-xs font-mono-ps text-[#9CA3AF] uppercase tracking-widest">Signed in</div>
                    <div className="text-sm text-white truncate">{user.email}</div>
                  </div>
                  <Link
                    to="/account"
                    data-testid="user-account-link"
                    onClick={() => setShowUser(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-white/85 hover:bg-[#C9A227]/10 hover:text-[#C9A227]"
                  >
                    <Settings size={15} /> Il mio account
                  </Link>
                  {user.is_admin && (
                    <Link
                      to="/admin"
                      data-testid="admin-link"
                      onClick={() => setShowUser(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#C9A227] hover:bg-[#C9A227]/10"
                    >
                      <Settings size={15} /> Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={() => { setShowUser(false); logout(); }}
                    data-testid="logout-button"
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-white/85 hover:bg-[#C9A227]/10 hover:text-[#C9A227] w-full text-left"
                  >
                    <LogOut size={15} /> Esci
                  </button>
                </div>
              )}
            </div>

            <Link
              to="/account?tab=wishlist"
              data-testid="wishlist-link"
              aria-label="Wishlist"
              className="p-2 text-white hover:text-[#C9A227] transition-colors relative"
            >
              <Heart size={19} />
              {wlCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#C9A227] text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {wlCount}
                </span>
              )}
            </Link>

            <button
              data-testid="cart-toggle"
              aria-label="Carrello"
              onClick={() => setOpen(true)}
              className="p-2 text-white hover:text-[#C9A227] transition-colors relative"
            >
              <ShoppingBag size={19} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#C9A227] text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Search bar */}
        {searchOpen && (
          <div className="border-t border-[#C9A227]/20 bg-[#0b0b0d]">
            <form onSubmit={doSearch} className="max-w-3xl mx-auto px-4 py-4 relative">
              <input
                data-testid="search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca prodotto, set, codice… (es. OP-09, Charizard)"
                className="w-full bg-black border border-[#C9A227]/30 focus:border-[#C9A227] rounded-sm px-4 py-3 text-white placeholder:text-[#6B7280] outline-none text-base"
              />
              {sugg.length > 0 && (
                <div className="absolute left-4 right-4 mt-1 bg-[#121212] border border-[#C9A227]/30 rounded-sm overflow-hidden z-50">
                  {sugg.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/prodotto/${s.slug}`}
                      onClick={() => setSearchOpen(false)}
                      data-testid={`suggest-${s.slug}`}
                      className="flex justify-between items-center px-4 py-2.5 text-sm text-white/85 hover:bg-[#C9A227]/10 hover:text-[#C9A227] border-b border-[#C9A227]/10 last:border-0"
                    >
                      <span className="truncate">{s.name}</span>
                      <span className="font-mono-ps text-[#C9A227] shrink-0 ml-3">€ {s.price.toFixed(2)}</span>
                    </Link>
                  ))}
                </div>
              )}
            </form>
          </div>
        )}
      </header>

      {/* Mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDrawer(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#0b0b0d] border-r border-[#C9A227]/20 p-5 overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <Logo />
              <button data-testid="mobile-menu-close" onClick={() => setDrawer(false)} className="p-2 text-white"><X size={20} /></button>
            </div>
            <nav className="flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setDrawer(false)}
                  end={n.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-3 text-sm font-mono-ps uppercase tracking-widest border-l-2 ${
                      isActive ? "text-[#C9A227] border-[#C9A227] bg-[#C9A227]/5" : "text-white/80 border-transparent hover:text-[#C9A227] hover:border-[#C9A227]/40"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </aside>
        </div>
      )}
    </>
  );
};
